/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Box,
  Slider,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox, {
  ModbusIUAddressSelectBox,
} from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {
  patchCompositionsMoubusUnit,
  getCompositionsMoubusUnitByOUID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {format, pullDownMenu, sliderMarks, limit} from '../../define';

/**
 * 室内機編集ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const IUEditDialog = (props: {
  /** 修正する室内機ID */
  iuID: string;
  /** 修正する室内機が登録されている室外機の情報 */
  ouInfo: apiIF.res.getCompositionsMoubusUnitByOUID | null;
  /** 修正する室内機が登録されている室外機の情報（set関数） */
  setOUInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsMoubusUnitByOUID | null>
  >;
  /** ダイアログを開くかどうか */
  isOpenDialog: boolean;
  /** ダイアログを開くかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID, gwID, ouID} = useParams();

  //編集前の室内機情報を取得
  let tempIUName = '';
  let tempIUAddress: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' = '1';
  let tempErrorThresholds = '5';
  let tempWarningThresholds = [-1, 1];
  if (props.ouInfo !== null) {
    for (const iuInfo of props.ouInfo['indoor-unit-informations']) {
      if (iuInfo['unit-id'] === props.iuID) {
        tempIUName = iuInfo['indoor-unit-name'];
        tempIUAddress = iuInfo.address;
        tempErrorThresholds = iuInfo['error-threshold'];
        tempWarningThresholds[0] = Number(
          iuInfo['temperature-boundary']['low-boundary']
        );
        tempWarningThresholds[1] = Number(
          iuInfo['temperature-boundary']['high-boundary']
        );
      }
    }
  }

  const [iuName, setIUName] = useState(tempIUName); //室内機の名前
  const [iuAddress, setIUAddress] = useState(tempIUAddress); //アドレス
  const [errorThresholds, setErrorThresholds] = useState(tempErrorThresholds); //庫内温度異常とするための閾値
  const [warningThresholds, setWarningThresholds] = useState(
    tempWarningThresholds
  ); //庫内温度と設定温度の温度差の警戒レベルを判定するための境界
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [iuNameError, setIUNameError] = useState(false); //室内機の名前

  //入力項目のチェック
  useEffect(() => {
    if (iuName === '' || iuNameError || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [iuName, iuNameError, apiWait]);

  /**
   * スライダー変更時処理
   * @param event
   * @param newValue
   * @param activeThumb
   * @returns
   */
  const handleChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }
    const minDistance = 1;

    if (activeThumb === 0) {
      //スライダーの値が許容する値より大きい場合、許容している最大値に変更する
      const adjustedNewMinValue =
        newValue[0] > limit.WARNING_THRESHOLDS_MIN_ALLOWED_MAX_VALUE
          ? limit.WARNING_THRESHOLDS_MIN_ALLOWED_MAX_VALUE
          : newValue[0];

      setWarningThresholds([
        Math.min(adjustedNewMinValue, warningThresholds[1] - minDistance),
        warningThresholds[1],
      ]);
    } else {
      //スライダーの値が許容する値より小さい場合、許容している最小値に変更する
      const adjustedNewMaxValue =
        newValue[1] < limit.WARNING_THRESHOLDS_MAX_ALLOWED_MIN_VALUE
          ? limit.WARNING_THRESHOLDS_MAX_ALLOWED_MIN_VALUE
          : newValue[1];

      setWarningThresholds([
        warningThresholds[0],
        Math.max(adjustedNewMaxValue, warningThresholds[0] + minDistance),
      ]);
    }
  };

  /**
   * OKボタン押下時処理
   * @returns
   */
  const editIU = () => {
    //パスパラメータを取得できない場合
    //室内外機の情報を取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      ouID === undefined ||
      props.ouInfo === null
    ) {
      return;
    }

    setAPIWait(true);

    //APIに送信するデータの作成
    const editData: apiIF.reqParameters.patchCompositionsMoubusUnit = {
      'indoor-unit-informations': props.ouInfo['indoor-unit-informations'].map(
        iuInfo => {
          if (iuInfo['unit-id'] === props.iuID) {
            const temp = {...iuInfo};
            temp['indoor-unit-name'] = iuName;
            temp.address = iuAddress;
            temp['error-threshold'] = errorThresholds;
            temp['temperature-boundary']['low-boundary'] = String(
              warningThresholds[0]
            );
            temp['temperature-boundary']['high-boundary'] = String(
              warningThresholds[1]
            );
            return temp;
          }
          return iuInfo;
        }
      ),
    };
    //情報を編集
    patchCompositionsMoubusUnit(siteID, gwID, ouID, editData)
      .then(() => {
        //室外機の情報を取得
        return getCompositionsMoubusUnitByOUID(siteID, gwID, ouID);
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['indoor-unit-informations'].sort(
          sortByUnicode('indoor-unit-name')
        );

        props.setOUInfo(response);
        props.setIsOpenDialog(false);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setIUName(tempIUName);
    setIUAddress(tempIUAddress);
    setErrorThresholds(tempErrorThresholds);
    setWarningThresholds(tempWarningThresholds);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('IUEditDialog.modbusUnit.title')}
        </DialogTitle>
        <DialogContent>
          {/** ダイアログに表示するメッセージ */}
          <DialogContentText>
            {trans('IUEditDialog.modbusUnit.msg')}
          </DialogContentText>
          <Stack spacing={2}>
            {/** 室内機名称 */}
            <ChkInputTextbox
              setValue={setIUName}
              setError={setIUNameError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={trans('IUEditDialog.modbusUnit.txtField.label.unitName')}
              value={iuName}
              id={`${tempIUName}editIuNameTextForm`}
              class="gwSetting__contents__textBox--unitName"
              helperText=" "
              errHelperText={trans(
                'IUEditDialog.modbusUnit.txtField.errHelperTxt'
              )}
              variant="standard"
              autoFocus={true}
              margin="dense"
              chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
            />
            {/** アドレス */}
            <ModbusIUAddressSelectBox
              setAddress={setIUAddress}
              label={trans('IUEditDialog.modbusUnit.txtField.label.address')}
              default={tempIUAddress}
              class="gwSetting__contents__textBox--address"
              id="selectIuUnit"
            />
            {/** 庫内温度異常とするための閾値 */}
            <TextFieldSelectBox
              label={trans(
                'IUEditDialog.modbusUnit.txtField.label.errThresholds'
              )}
              value={errorThresholds}
              setValue={setErrorThresholds}
              choices={pullDownMenu.ERROR_THRESHOLDS}
              class="gwSetting__contents__selectBox--errorThresholds"
            />
            {/** 庫内温度と設定温度の温度差の警戒レベルを判定するための境界 */}
            <Stack spacing={1} className="gwSetting__contents__slider">
              <Box className="gwSetting__contents__slider__title">
                {trans('IUEditDialog.modbusUnit.slider.title')}
              </Box>
              <Box className="gwSetting__contents__slider__input">
                <Slider
                  getAriaLabel={() => 'Warning thresholds'}
                  getAriaValueText={value => `${value}°C`}
                  disableSwap
                  marks={sliderMarks.WARNING_THRESHOLDS.map(mark => {
                    return {
                      ...mark,
                      label: `${mark.label}${trans(
                        'IUEditDialog.modbusUnit.slider.unit'
                      )}`,
                    };
                  })}
                  max={limit.WARNING_THRESHOLDS_MAX}
                  min={limit.WARNING_THRESHOLDS_MIN}
                  onChange={handleChange}
                  step={1}
                  value={warningThresholds}
                  valueLabelDisplay="auto"
                />
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('IUEditDialog.buttonTxt.cancel')}
            id={`${tempIUName}EditCancelBtn`}
          />
          <PrimaryButton
            onClick={editIU}
            buttonText={trans('LayoutSettingDialog.dialogBox.buttonTxt.save')}
            id={`${tempIUName}EditOkBtn`}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${tempIUName}EditErrDlgBtn`}
      />
    </>
  );
};
export default IUEditDialog;
